import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import SimpleUserCard from '../../components/SimpleUserCard';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import {
  defaultAssessmentTypes,
  otherTemplateTypes,
  paths,
  qualityTemplateTypes,
} from '../../constants/strings.js';
import { Icon } from '@mui/material';
import { useWorkspace } from '../../providers/WorkspaceProvider.jsx';
import { getRoute } from '../../constants/strings.js';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys.js';
import { getColor } from '../../constants/strings';
import {
  useTableComponents,
  AutocompleteFilter,
  CustomDateRangeFilter,
} from '../../components/tables/MTableComponents.jsx';
import { customColors } from '../../utilities/misc.js';
import { useModal } from '../../providers/ModalProvider.jsx';
import { modals } from '../../providers/modals.js';
import { useCurrentUser } from '../../providers/UserProvider.jsx';
import { templateModuleKeys } from '../../components/permission_manager/utilities.js';
import { useQueryParams } from '../../hooks/misc.js';
import useCheckPermission from '../../providers/UsePermissionHook.jsx';
import { actions } from '../../utilities/misc.js';
import MTTable from '../../components/tables/MTable.jsx';
import { useURLParams } from '../../providers/URLParamProvider.jsx';
import { getDateRange, dateTypes } from '../../components/tables/table.js';

const templatesQuery = loader('./TemplatesList.graphql');

export default function TemplatesList() {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { filters, page, pageSize, sort, cachedDateFilters } = useURLParams();
  const { getParam } = useQueryParams();
  const segment = window.location.pathname.split('/')[3];

  const [dateSelectors, setDateSelectors] = useState(() => {
    return dateTypes.reduce((acc, [field, minKey, maxKey]) => {
      const [min, max] = getDateRange(
        cachedDateFilters?.[field],
        getParam(field),
      );
      acc[minKey] = min;
      acc[maxKey] = max;
      return acc;
    }, {});
  });

  const { t } = useTranslation();
  const {
    labelCell,
    dateCell,
    textCell,
    userFilterOptions,
    workspaceFilterOptions,
    workspaceCell,
  } = useTableComponents();

  const { openModal } = useModal();
  const { user, isAdmin, assessmentTypes, allTemplateTypes } = useCurrentUser();
  // Quality module only: one COMPANY_WIDE grant covers QUALITY and MOC
  const hasCompanyWideAccess =
    useCheckPermission({
      model: 'inspectionTemplate',
      action: actions.companyWide,
      type: 'QUALITY',
    }) && segment === 'quality';

  const allowedTypes = useMemo(() => {
    if (segment === 'quality') {
      return Object.values(defaultAssessmentTypes)
        .filter((t) => qualityTemplateTypes.includes(t.key))
        .map((t) => ({ label: t.title || t.name, value: t.key, ...t }));
    } else if (!isAdmin) {
      const types = [];
      const roles = user?.workRoles || [];

      for (const role of roles) {
        const permissions = role.permissions?.inspectionTemplate ?? [];
        for (const permission of permissions) {
          types.push(permission.split('|')[1]);
        }
      }
      const flattenedTypes = Array.from(new Set(types));
      return Object.values(assessmentTypes)
        .map((type) => flattenedTypes.includes(type.key) && type)
        .filter(({ key }) => !templateModuleKeys.includes(key))
        .filter(Boolean);
    }
    return Object.values(assessmentTypes)
      .map((type) => type)
      .filter(({ key }) => !otherTemplateTypes.includes(key));
  }, [segment, isAdmin, assessmentTypes, user?.workRoles]);
  const type = getParam('templateType') || getParam('lockedType');
  const extraFilters = useMemo(() => {
    if (!!type) {
      return { field: 'templateType', operator: 'eq', value: type };
    } else if (segment === 'quality') {
      return {
        field: 'templateType',
        operator: 'in',
        value: ['QUALITY', 'MOC'],
      };
    } else {
      return {
        field: 'templateType',
        operator: 'not',
        value: otherTemplateTypes,
      };
    }
  }, [segment, type]);

  const {
    refetch,
    loading,
    data: { inspectionTemplates = [], inspectionTemplatesCount = 0 } = {},
  } = useQuery(templatesQuery, {
    skip: !page || !pageSize || !filters || !sort || !workspace?.id,
    variables: {
      options: {
        page,
        pageSize,
        sort,
        filters: [
          ...filters,
          { field: 'isArchived', operator: 'eq', value: ['false'] },
          { field: 'internalType', operator: 'eq', value: 'null' },
          extraFilters,
        ],
      },
    },
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: '',
        enableColumnFilter: false,
        enableSorting: false,
        enableRowClick: false,
        size: 30,
        Cell: ({ row }) => {
          const type = row.original.templateType.toLowerCase();
          const isDraft = row.original.isDraft;
          return isDraft ? null : (
            <Icon
              style={{
                color: customColors.BLUE,
                fontSize: '1rem',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                openModal({
                  modalName: modals.createAssessment,
                  variables: {
                    types: [
                      assessmentTypes[type === 'vehicle' ? 'fleet' : type],
                    ],
                    title: t(keys.incidents.START_INCIDENT_ASSESSMENT),
                    type: assessmentTypes[type === 'vehicle' ? 'fleet' : type],
                    participants: [user],
                    template: row.original,
                  },
                });
              }}
            >
              add
            </Icon>
          );
        },
      },
      {
        accessorKey: 'templateType',
        header: t(keys.common.TYPE),
        size: 100,
        filterVariant: 'select',
        filterSelectOptions: allowedTypes.map((type) => ({
          value: type.key,
          label: type?.title ? t(type.title) : type?.name,
        })),
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              fontSize: '1.25rem',
              color: getColor(
                cell?.getValue()?.toLowerCase(),
                allTemplateTypes,
              ),
            }}
          >
            {allTemplateTypes[cell.getValue().toLowerCase()]?.icon ||
              'local_shipping'}
          </Icon>
        ),
      },
      {
        accessorKey: 'title',
        header: t(keys.common.TITLE),
        grow: true,
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'creator',
        header: t(keys.common.CREATOR),
        grow: true,
        enableSorting: false,
        Cell: ({ cell }) => <SimpleUserCard size="sm" user={cell.getValue()} />,
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={userFilterOptions(isAdmin ? 'all' : 'workspace')}
            type="user"
            filterBy={t(keys.common.CREATOR)}
            column={column}
          />
        ),
      },
      {
        accessorKey: 'isDraft',
        header: t(keys.common.STATUS),
        grow: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.DRAFT) },
          { value: 'false', label: t(keys.common.PUBLISHED) },
        ],
        Cell: ({ cell }) =>
          labelCell({
            name: !!cell.getValue()
              ? t(keys.common.DRAFT)
              : t(keys.common.PUBLISHED),
            color: !!cell.getValue() ? 'yellow' : 'green',
          }),
      },
      {
        accessorKey: 'revisionNumber',
        header: t(keys.common.REV),
        grow: true,
        enableSorting: false,
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'documentString',
        header: t(keys.templates.FORM_ID),
        grow: true,
        enableSorting: false,
        Cell: ({ cell }) => textCell({ cell }),
      },
      {
        accessorKey: 'workspace',
        header: t(keys.common.WORKSPACE),
        grow: true,
        enableSorting: false,
        Cell: ({ cell }) => workspaceCell({ cell }),
        Filter: ({ column }) => (
          <AutocompleteFilter
            items={workspaceFilterOptions({
              includePublic: true,
              hasCompanyWideAccess,
            })}
            type="workspace"
            filterBy={t(keys.common.WORKSPACE)}
            column={column}
          />
        ),
      },
      {
        accessorKey: 'analysisRequested',
        header: t(keys.templates.SECOND_SIGHT),
        minSize: 150,
        grow: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.YES) },
          { value: 'false', label: t(keys.common.NO) },
        ],
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              color: cell.getValue() ? '#28a745' : '#6c757d',
              fontSize: '1.25rem',
            }}
          >
            {!!cell.getValue() ? 'check' : 'close'}
          </Icon>
        ),
      },
      {
        accessorKey: 'reviewRequired',
        header: t(keys.templates.APPROVAL_REQUIRED),
        minSize: 150,
        grow: true,
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.YES) },
          { value: 'false', label: t(keys.common.NO) },
        ],
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              color: cell.getValue() ? '#28a745' : '#6c757d',
              fontSize: '1.25rem',
            }}
          >
            {!!cell.getValue() ? 'check' : 'close'}
          </Icon>
        ),
      },
      {
        accessorKey: 'dateModified',
        header: t(keys.common.LAST_UPDATED),
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.dateModifiedMin}
            maxDate={dateSelectors.dateModifiedMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateModifiedMax: value })
            }
          />
        ),
      },
      {
        accessorKey: 'dateCreated',
        header: t(keys.common.DATE_CREATED),
        size: 275,
        Cell: ({ cell }) => dateCell({ cell }),
        Filter: ({ column }) => (
          <CustomDateRangeFilter
            column={column}
            minDate={dateSelectors.dateCreatedMin}
            maxDate={dateSelectors.dateCreatedMax}
            setMinDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateCreatedMin: value })
            }
            setMaxDate={(value) =>
              setDateSelectors({ ...dateSelectors, dateCreatedMax: value })
            }
          />
        ),
      },
    ],
    [
      t,
      allowedTypes,
      openModal,
      assessmentTypes,
      user,
      allTemplateTypes,
      textCell,
      userFilterOptions,
      isAdmin,
      labelCell,
      workspaceCell,
      workspaceFilterOptions,
      hasCompanyWideAccess,
      dateCell,
      dateSelectors,
    ],
  );

  return (
    <MTTable
      refetch={refetch}
      isLoading={loading}
      rightClickNavigation={paths.templates}
      columns={columns}
      data={inspectionTemplates ?? []}
      rowCount={inspectionTemplatesCount ?? 0}
      onRowClick={(template) =>
        navigate(getRoute(workspace?.id, paths.templates, template.id))
      }
      onClearAllFilters={() => setDateSelectors({})}
      enableRowVirtualization
      mrtVirtualizerOptions={{ overscan: 5 }}
      showAllWorkspacesCheck
    />
  );
}
